import { ApplicationDMQueueParams, ApplicationProvider, OperPostProvider } from '.'
import { Result, failure, success } from '../shared'
import {
  ApplicationAddAction,
  ApplicationFailure,
  ApplicationServiceErrorCode,
  OperActions,
  OperPostActions,
  SearchPages,
  ViewPages,
} from './models/application.types'
import { CommitteeParams } from './models/committee-params.models'
import { ApplicationOperParams } from './models/oper/oper-params.model'
import { ApplicationOperPostParams } from './models/oper/oper_post-params.model'
import { SearchParams } from './models/search/search-params.model'
import { ApplicationViewParams } from './models/view/view-params.model'
import { CommitteeProvider } from './providers/committee.provider'
import { OperProvider } from './providers/oper.provider'
import { SearchProvider } from './providers/search.provider'
import { ViewProvider } from './providers/view.provider'
import { ApplicationAddProvider } from './providers/add.provider'
import {
  ApplicationAddParams,
  ApplicationAddResponse,
  ApplicationDeleteParams,
  ApplicationDeleteResponse,
  ApplicationEditParams,
  ApplicationEditResponse,
} from './models/application/application-add.params.model'

export class ApplicationService {
  constructor(
    private readonly committeProvider: CommitteeProvider,
    private readonly searchProvider: SearchProvider,
    private readonly viewProvider: ViewProvider,
    private readonly operProvider: OperProvider,
    private readonly operPostProvider: OperPostProvider,
    private readonly applicationProvider: ApplicationProvider,
    private readonly applicationAddProvider: ApplicationAddProvider,
  ) {}

  public async oper(action: OperActions, params: ApplicationOperParams) {
    try {
      const { data } = await this.operProvider.oper(action, params)
      return success(data)
    } catch (error) {
      return failure(Number(error.Code), {
        message: error.Message || error.Text,
      })
    }
  }

  public async operPost(action: OperPostActions, params: ApplicationOperPostParams) {
    try {
      const { data } = await this.operPostProvider.oper_post(action, params)
      return success(data)
    } catch (error) {
      return failure(Number(error.Code), {
        message: error.Message || error.Text,
      })
    }
  }

  public async view(page: ViewPages, params: ApplicationViewParams) {
    try {
      const { data } = await this.viewProvider.view(page, params)
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async search(page: SearchPages, params: SearchParams) {
    try {
      const { data } = await this.searchProvider.search(page, params)
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async approveCommittee(
    id: number,
    hash: string,
    rest: Record<string, unknown>,
  ): Promise<Result<any, ApplicationFailure>> {
    try {
      const { data } = await this.committeProvider.committiee(
        'approve',
        {
          AppID: id,
          HashSum_AppID_: hash,
        },
        rest,
      )
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async validateCommittee(id: number, hash: string): Promise<Result<any, ApplicationFailure>> {
    try {
      const { data } = await this.committeProvider.committiee('validate', {
        AppID: id,
        HashSum_AppID_: hash,
      })
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deleteCommittee(params: CommitteeParams): Promise<Result<any, ApplicationFailure>> {
    try {
      const { data } = await this.committeProvider.committiee('delete', {
        ...params,
      })
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async viewCommittee(params: CommitteeParams): Promise<Result<any, ApplicationFailure>> {
    try {
      const { data } = await this.committeProvider.committiee('view', {
        ...params,
      })
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async viewCustFieldsCommittee(params: CommitteeParams): Promise<Result<any, ApplicationFailure>> {
    try {
      const { data } = await this.committeProvider.committiee('viewcustfields', {
        ...params,
      })
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async rejectCommittee(
    params: CommitteeParams,
    rest: Record<string, unknown>,
  ): Promise<Result<any, ApplicationFailure>> {
    try {
      const { data } = await this.committeProvider.committiee(
        'reject',
        {
          ...params,
        },
        rest,
      )
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async suspendCommittee(
    params: CommitteeParams,
    rest: Record<string, unknown>,
  ): Promise<Result<any, ApplicationFailure>> {
    try {
      const { data } = await this.committeProvider.committiee(
        'suspend',
        {
          ...params,
        },
        rest,
      )
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async dmQueue(params: ApplicationDMQueueParams): Promise<Result<any, ApplicationFailure>> {
    try {
      const { data } = await this.applicationProvider.dmQueue(params)
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async applicationAdd(
    params: ApplicationAddParams,
  ): Promise<Result<ApplicationAddResponse, ApplicationFailure>> {
    try {
      const { data } = await this.applicationAddProvider.changeEssence<ApplicationAddResponse>('add', params)
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async applicationEdit(
    params: ApplicationEditParams,
  ): Promise<Result<ApplicationEditResponse, ApplicationFailure>> {
    try {
      const { data } = await this.applicationAddProvider.changeEssence<ApplicationEditResponse>('edit', params)
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async applicationDelete(
    params: ApplicationDeleteParams,
  ): Promise<Result<ApplicationDeleteResponse, ApplicationFailure>> {
    try {
      const { data } = await this.applicationAddProvider.changeEssence<ApplicationDeleteResponse>('delete', params)
      return success(data)
    } catch (error) {
      return failure(ApplicationServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }
}
